import React, { Provider, useContext } from 'react'
import Head from 'next/head'
import { decode } from 'html-entities'
import { BLOCKS, Document } from '@contentful/rich-text-types'

import { CDN_URL } from '../../constants'
import { TypeSeoMetadata } from '@lib/types'
import { withPublicAssetUrl } from '@lib/utils/cdn'
import { FontFamily, getFontsToPreload, getStyleFontFaces } from '../fonts'
import { TypeSocialMetadata } from '@lib/types/social-metadata'
import { composeUrl } from '@lib/utils/url'
import { PageContext } from '@contexts/page-context'
import {
  CitationProgrammaticPageContextProvider,
  useCitationProgrammaticPageContext,
} from '@contexts/citation-programmatic-page-context'
import { renderRichTextToHtmlString } from '../renderer/rich-text'
import { getConfig } from '../../config'
import { updateAssetCDNUrl } from '../asset/utils'
import { SchemaMarkupScript } from '@lib/tracking/schema-markup'
import { isEligibleForSchemaMarkup } from '@lib/utils/page-rules/by-slug'
import { useLanguage } from '@lib/hooks'

const baseUrl = getConfig().urls.main

const defaultTitle = 'Grammarly'

const faviconsOriginal: Array<{ key: string; type: string; sizes?: string; href: string }> = [
  { key: 'default', type: 'image/x-icon', href: withPublicAssetUrl('favicon.ico') },
  { key: '16x16', type: 'image/png', sizes: '16x16', href: withPublicAssetUrl('favicon-16x16.png') },
  { key: '32x32', type: 'image/png', sizes: '32x32', href: withPublicAssetUrl('favicon-32x32.png') },
]

type PageHeadProps = {
  seoMetadata?: TypeSeoMetadata
  socialMetadata?: TypeSocialMetadata
}

const socialOrSeo = (social: string, seo: string): string => {
  return social.trim() === '' ? seo : social
}

const renderRichTextToString = <C extends object | undefined>(
  richText: Document,
  ContextProvider: Provider<C>,
  contextValue: C
) => {
  return renderRichTextToHtmlString(
    richText,
    {
      [BLOCKS.PARAGRAPH]: (node, next) => {
        // decode html entity characters for Title and Description metadata fields
        return decode(next(node.content))
      },
    },
    ContextProvider,
    contextValue
  )
}

export const PageHead: React.FC<PageHeadProps> = ({ seoMetadata, socialMetadata }) => {
  const { defaultLang, lang, supportedLangs, isDefaultLang } = useLanguage()
  const { slug } = useContext(PageContext)
  const fontFamilies: FontFamily[] = ['Inter', 'Matter', 'MatterforGrammarly', 'Glyph', 'MessinaSerifWeb']
  const contextValue = useCitationProgrammaticPageContext()

  // Remove language prefix from slug if present (e.g., 'es/ai-humanizer' -> 'ai-humanizer')
  const slugWithoutLang = supportedLangs.reduce((currentSlug, supportedLang) => {
    const prefix = `${supportedLang}/`
    return currentSlug.startsWith(prefix) ? currentSlug.substring(prefix.length) : currentSlug
  }, slug)

  const {
    titleRichText,
    descriptionRichText,
    keywords = [],
    canonicalUrl = `${baseUrl}/${isDefaultLang ? '' : lang + '/'}${slugWithoutLang}`, // default to self-reference canonical url
    noindex,
  } = seoMetadata?.fields || {}
  const robots = [noindex ? 'noindex' : undefined].filter((x) => x !== undefined)

  const seoTitle = titleRichText
    ? renderRichTextToString(titleRichText, CitationProgrammaticPageContextProvider, contextValue)
    : defaultTitle

  const seoDescription = descriptionRichText
    ? renderRichTextToString(descriptionRichText, CitationProgrammaticPageContextProvider, contextValue)
    : ''

  const {
    titleRichText: socialTitleRichText,
    descriptionRichText: socialDescriptionRichText,
    url: ogUrlString = '',
    type: ogType,
    image: ogImage,
  } = socialMetadata?.fields || {}

  const ogTitleString = socialTitleRichText
    ? renderRichTextToString(socialTitleRichText, CitationProgrammaticPageContextProvider, contextValue)
    : ''

  const ogDescriptionString = socialDescriptionRichText
    ? renderRichTextToString(socialDescriptionRichText, CitationProgrammaticPageContextProvider, contextValue)
    : ''

  const ogTitle = socialOrSeo(ogTitleString, seoTitle)
  const ogDescription = socialOrSeo(ogDescriptionString, seoDescription)
  const ogUrl = socialOrSeo(ogUrlString, canonicalUrl)

  const ogImageRelativeUrl = updateAssetCDNUrl(ogImage?.fields.asset?.fields.file?.url ?? '')
  const ogImageUrl =
    (ogImageRelativeUrl && composeUrl(ogImageRelativeUrl)) || withPublicAssetUrl('open-graph/social-24.png')

  const social = (
    <>
      {/**
       * Be careful: tags must be either direct children of the `Head` component or wrapped
       * in a maximum of one level of React.Fragment or arrays, otherwise they will not be
       * picked up on the client side.
       * https://nextjs.org/docs/api-reference/next/head
       */}
      {ogTitle.trim() && <meta property="og:title" content={ogTitle} />}
      <meta property="og:type" content={ogType} />
      {ogDescription.trim() && <meta property="og:description" content={ogDescription} />}
      {ogUrl.trim() && <meta property="og:url" content={ogUrl} />}
      {ogImageUrl && <meta property="og:image" content={ogImageUrl} />}
      {ogImageUrl && <meta name="twitter:card" content="summary_large_image" />}
      <meta name="twitter:site" content="@Grammarly" />
    </>
  )

  const supportedLangsWithoutDefaultLang = supportedLangs.filter((lang) => lang !== defaultLang)
  const hrefLang =
    supportedLangsWithoutDefaultLang.length > 0 ? (
      <>
        <link rel="alternate" href={`${baseUrl}/${slugWithoutLang}`} hrefLang="x-default" />
        <link rel="alternate" href={`${baseUrl}/${slugWithoutLang}`} hrefLang={defaultLang} />
        {supportedLangsWithoutDefaultLang.map((supportedLang) => {
          return (
            <link
              key={supportedLang}
              rel="alternate"
              href={`${baseUrl}/${supportedLang}/${slugWithoutLang}`}
              hrefLang={supportedLang}
            />
          )
        })}
      </>
    ) : null

  return (
    <Head>
      <title>{seoTitle}</title>
      {robots.length > 0 && <meta name="robots" content={robots.join(', ')} key="robots" />}
      {seoDescription.trim() && <meta name="description" content={seoDescription} key="description" />}
      {canonicalUrl.trim() && <link rel="canonical" href={canonicalUrl} key="canonical" />}
      {hrefLang}
      {social}
      {keywords.length > 0 && <meta name="keywords" content={keywords.join(',')} key="keywords" />}
      {CDN_URL && (
        <>
          <link rel="preconnect" href={CDN_URL} crossOrigin="anonymous" />
          <link rel="dns-prefetch" href={CDN_URL} />
        </>
      )}
      {getFontsToPreload(fontFamilies).map(({ key, url, format }) => (
        <link key={key} rel="preload" as="font" href={url} type={`font/${format}`} crossOrigin="anonymous" />
      ))}
      {faviconsOriginal.map(({ key, ...restProps }) => (
        <link key={key} rel="icon" {...restProps} />
      ))}
      <script
        key="facade-data"
        type="text/javascript"
        dangerouslySetInnerHTML={{
          __html: 'function handleFacadeExperimentInfo(data) { window.Grammarly = data || {} }',
        }}
      />
      {/* Empty tag will be replaced on the server-side*/}
      {!IS_CLIENT_SIDE && <script src="/api/participate/redirect?callback=handleFacadeExperimentInfo" async></script>}
      <style dangerouslySetInnerHTML={{ __html: getStyleFontFaces(fontFamilies) }} />
      {isEligibleForSchemaMarkup(slug) && SchemaMarkupScript({ name: seoTitle, description: seoDescription })}
    </Head>
  )
}
